import React, { useCallback, useEffect, useState } from 'react';
import { Crown, Medal, Eye } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import GamificationAchievementsModal from '../components/GamificationAchievementsModal';
import { useAuth } from '../context/AuthContext';
import { gamificationAPI } from '../services/api';

const POLL_INTERVAL_MS = 30000;

const currentMonth = () => new Date().toISOString().slice(0, 7);

const medalColor = (position) => {
  if (position === 0) return 'text-amber-300';
  if (position === 1) return 'text-slate-300';
  if (position === 2) return 'text-orange-400';
  return 'text-slate-500';
};

const LoadingRows = () => (
  <div className="space-y-2">
    {[...Array(5)].map((_, index) => (
      <div key={`ranking-skeleton-${index}`} className="grid grid-cols-12 gap-3 items-center rounded-lg border border-gray-800 bg-gray-900 px-4 py-3">
        <Skeleton className="h-4 col-span-1 bg-gray-800" />
        <Skeleton className="h-4 col-span-4 bg-gray-800" />
        <Skeleton className="h-4 col-span-2 bg-gray-800" />
        <Skeleton className="h-4 col-span-2 bg-gray-800" />
        <Skeleton className="h-4 col-span-2 bg-gray-800" />
      </div>
    ))}
  </div>
);

const GamificationRankingPage = () => {
  const { user: currentUser } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const month = currentMonth();

  const loadRanking = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const response = await gamificationAPI.getMemberRanking(month);
      setRows(response.data || []);
    } catch (error) {
      toast.error('Não foi possível carregar o ranking.');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    loadRanking(true);
    const intervalId = setInterval(() => loadRanking(false), POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [loadRanking]);

  const canViewAchievements = (row) => currentUser?.role === 'ADMIN' || row.user_id === currentUser?.id;

  return (
    <Layout>
      <div className="space-y-6" data-testid="gamification-ranking-page">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <Crown className="text-amber-300" />
            Ranking do Mês
          </h1>
          <p className="text-gray-400 mt-1">Acompanhe as conquistas de toda a Elite, atualizado automaticamente.</p>
        </div>

        <Card className="border-gray-800 bg-gray-900">
          <CardHeader>
            <CardTitle className="text-white">Pontuação de {new Date(`${month}-01T00:00:00`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <LoadingRows />
            ) : rows.length === 0 ? (
              <div className="rounded-lg border border-gray-800 bg-gray-950 px-4 py-6 text-center text-gray-300">
                Nenhuma conquista pontuada neste mês ainda.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="border-b border-gray-800 text-gray-400 text-left">
                      <th className="px-3 py-3 font-medium w-14">#</th>
                      <th className="px-3 py-3 font-medium">Membro</th>
                      <th className="px-3 py-3 font-medium">Pts Membro</th>
                      <th className="px-3 py-3 font-medium">Conquistas</th>
                      <th className="px-3 py-3 font-medium">Pts Evento</th>
                      <th className="px-3 py-3 font-medium">Total</th>
                      <th className="px-3 py-3 font-medium w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={row.user_id} className="border-b border-gray-800/80 text-gray-200">
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1">
                            {index < 3 ? <Medal size={16} className={medalColor(index)} /> : null}
                            <span className="font-medium text-white">{index + 1}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 font-medium text-white">
                          {row.full_name}
                          {row.user_id === currentUser?.id && (
                            <span className="ml-2 text-xs text-indigo-300">(você)</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-gray-300">{(row.member_points || 0).toLocaleString('pt-BR')} pts</td>
                        <td className="px-3 py-3 text-gray-300">{row.conquistas || 0}</td>
                        <td className="px-3 py-3 text-gray-300">{(row.event_points || 0).toLocaleString('pt-BR')} pts</td>
                        <td className="px-3 py-3">
                          <Badge className="border border-indigo-400/30 bg-indigo-950/70 text-indigo-200">
                            {(row.total_points || 0).toLocaleString('pt-BR')} pts
                          </Badge>
                        </td>
                        <td className="px-3 py-3">
                          {canViewAchievements(row) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-neutral-400 hover:text-white"
                              onClick={() => setSelectedUser(row)}
                              aria-label={`Ver conquistas de ${row.full_name}`}
                            >
                              <Eye size={16} />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <GamificationAchievementsModal
        open={!!selectedUser}
        onOpenChange={(open) => !open && setSelectedUser(null)}
        userId={selectedUser?.user_id}
        userName={selectedUser?.full_name}
        month={month}
        onRejected={() => loadRanking(false)}
      />
    </Layout>
  );
};

export default GamificationRankingPage;
